import Modal from "common/Modal/Modal";
import ConnectTelegram from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/ConnectTelegram";
import ConnectDiscord from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/ConnectDiscord";
import React, { useContext, useEffect, useState } from "react";
import useGetBasicDetailsFromUuid from "@shared/features/onboarding/hooks/useGetBasicDetailsFromUuid";
import ValidateConnectTelegram from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/ValidateConnectTelegram";
import ExternalAccountExtraInfo from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/ExternalAccountExtraInfo";
import {
  ExternalAccountType,
  ExtraInfo,
} from "@shared/features/onboarding/types";
import { OnboardingStyles } from "features/onboarding/common/styles";
import { getExternalAccountTypeDisplay } from "@shared/features/onboarding/utils";
import ConnectExternalAccountTopSection from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/ConnectExternalAccountTopSection";
import localStorageService from "utils/localStorageService";
import { CONNECT_TELEGRAM_KEY } from "@shared/features/onboarding/constants";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import HighlightedText from "common/Text/HighlightedText";
import { openMessage } from "utils/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

interface ConnectExternalAccountModalProps {
  externalAccountType?: ExternalAccountType;
  visible?: boolean;
  onCancel?: () => void;
  onSuccess?: () => void;
  uuid?: string;
  code?: string;
}

const ConnectExternalAccountModal = ({
  externalAccountType: propsExternalAccountType,
  visible,
  onCancel,
  onSuccess: propsOnSuccess,
  uuid: propsUuid,
  code,
}: ConnectExternalAccountModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SETTINGS);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { analytics } = useContext(AnalyticsContext);
  const [uuid, setUuid] = useState<string>(propsUuid || "");
  const [externalAccountType, setExternalAccountType] =
    useState<ExternalAccountType>(
      propsExternalAccountType || ExternalAccountType.TELEGRAM
    );
  const [connectTelegramStep, setConnectTelegramStep] =
    useState<ConnectTelegramStep>(ConnectTelegramStep.CONNECT);

  const accountType = propsExternalAccountType || externalAccountType;

  const getBasicDetailsFromUuidData = useGetBasicDetailsFromUuid(
    openMessage,
    uuid,
    accountType !== ExternalAccountType.TELEGRAM
  );

  useEffect(() => {
    analytics?.connectExternalAccountView({
      onboardingStep: undefined,
    });
  }, [analytics]);

  const onConnectExternalAccount = (
    externalAccountType: ExternalAccountType
  ) => {
    analytics?.connectToExternalAccountClicked({
      onboardingStep: undefined,
      externalAccountType: externalAccountType,
    });
  };

  const onCloseModal = () => {
    onCancel?.();
    localStorageService.removeValue(CONNECT_TELEGRAM_KEY);
  };

  const onSuccess = () => {
    propsOnSuccess?.();
    onCloseModal();
  };

  useEffect(() => {
    if (code) setConnectTelegramStep(ConnectTelegramStep.VALIDATE);
  }, [code]);

  const isTelegram = accountType === ExternalAccountType.TELEGRAM;
  const isValidateTelegram =
    isTelegram && connectTelegramStep === ConnectTelegramStep.VALIDATE;
  const getContent = () => {
    if (isValidateTelegram) {
      return (
        <ValidateConnectTelegram
          uuid={uuid}
          setUuid={setUuid}
          getBasicDetailsFromUuidData={getBasicDetailsFromUuidData}
          onCancel={() => setConnectTelegramStep(ConnectTelegramStep.CONNECT)}
          onSuccess={onSuccess}
          code={code}
          inModal={true}
        />
      );
    } else if (isTelegram) {
      return (
        <>
          <ConnectTelegram
            uuid={uuid}
            setUuid={setUuid}
            getBasicDetailsFromUuidData={getBasicDetailsFromUuidData}
            onContinueClick={() => {
              setConnectTelegramStep(ConnectTelegramStep.VALIDATE);
              onConnectExternalAccount(ExternalAccountType.TELEGRAM);
            }}
          />
          <ExternalAccountExtraInfo
            selectedAccountType={ExternalAccountType.TELEGRAM}
            cardsDisplay={true}
            hideExtraInfo={ExtraInfo.WHY_NEED}
          />
        </>
      );
    } else if (accountType === ExternalAccountType.DISCORD) {
      return (
        <ConnectDiscord
          onSuccess={() => {
            onSuccess();
            onConnectExternalAccount(ExternalAccountType.DISCORD);
          }}
        />
      );
    }
  };

  const getTitle = () => {
    if (
      accountType === ExternalAccountType.TELEGRAM &&
      connectTelegramStep === ConnectTelegramStep.VALIDATE
    )
      return t("external_accounts.validate_telegram");
    const typeDisplay = getExternalAccountTypeDisplay(propsExternalAccountType);
    return t("external_accounts.connect_account", { type: typeDisplay });
  };

  return (
    <Modal
      open={visible}
      onCancel={onCloseModal}
      footer={false}
      styles={{
        body: { padding: isMobile ? 24 : 32 },
      }}
      width={500}
      destroyOnHidden={true}
    >
      {!isValidateTelegram && (
        <OnboardingStyles.ModalTitle style={{ marginBottom: 24 }}>
          {getTitle()}
        </OnboardingStyles.ModalTitle>
      )}
      {connectTelegramStep !== ConnectTelegramStep.VALIDATE && (
        <HighlightedText style={{ marginBottom: 20 }}>
          {t("external_accounts.why_connect_description")}
        </HighlightedText>
      )}
      {!propsExternalAccountType &&
        connectTelegramStep !== ConnectTelegramStep.VALIDATE && (
          <ConnectExternalAccountTopSection
            accountType={externalAccountType}
            setAccountType={setExternalAccountType}
            style={{ marginBottom: 20 }}
            showEmptyState={false}
          />
        )}
      {getContent()}
    </Modal>
  );
};

export default ConnectExternalAccountModal;

enum ConnectTelegramStep {
  CONNECT,
  VALIDATE,
}
